import {
  ChakraBox,
  FireflyButton,
  ModalV2,
  Text,
  SkeletonLoader,
  ChakraHStack,
  ChakraVStack,
  FAIconName,
  Tab,
  useToastMessage,
} from '@equip.health/ui';
import { getGroupedTimeSlots } from '~/lib/util/inquiry.util';
import { useMemo, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import TimeSlot from '~/components/maud/providerAssignment/TimeSlotSelection/TimeSlot';
import { useLazyGetAllProvidersV2Query } from '~/store/services/providers';
import { useGetProviderAvailabilityV2Query } from '~/store/services/schedule';
import { useGetPatientContactsByInquiryIdQuery } from '~/store/services/patients';

import AppointmentSummary from './AppointmentSummary';
import { Recurrence, SYSTEM_TIME_ZONE } from '~/lib/util/calendar.util';
import {
  AppointmentStatus,
  RecurringAppointmentUpdateOption,
} from '~/lib/constants/calendar.constants';
import {
  useCancelAppointmentMutation,
  useSaveAppointmentMutation,
} from '~/store/services/appointment';
import { AppointmentTypeName } from '~/lib/constants/scheduling.constants';
import { toISO } from '~/lib/utils';
import {
  buildTimeZoneOptions,
  renderModalTitle,
  toValidTimeZone,
} from './helpers';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { InquiryEvents } from '~/analytics/inquiryEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

type Steps = 'slotSelection' | 'confirmation';

interface AdmissionConsultationModalProps {
  patient: Patient;
  inquiryExternalId: string;
  isOpen: boolean;
  onClose: () => void;
  editingAppointmentID?: string;
}

const AdmissionConsultationModal = ({
  patient,
  inquiryExternalId,
  isOpen,
  onClose,
  editingAppointmentID,
}: AdmissionConsultationModalProps) => {
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>();

  const [currentStep, setCurrentStep] = useState<Steps>('slotSelection');

  const [selectedSlot, setSelectedSlot] = useState<{
    providerId: string;
    startTime: string;
    endTime: string;
    slotId: string;
  }>();

  const [providerInfo, setProviderInfo] = useState<GetProviderByIdResponse[]>(
    [],
  );

  const [getAllProviderDetails, { isFetching: isProviderDetailsLoading }] =
    useLazyGetAllProvidersV2Query();

  const [saveAppointment, { isLoading: isSaveAppointmentLoading }] =
    useSaveAppointmentMutation();

  const [deleteAppointment, { isLoading: isCancelAppointmentLoading }] =
    useCancelAppointmentMutation();

  const isSubmitting = isSaveAppointmentLoading || isCancelAppointmentLoading;

  const { data: inquiryContactsResponse } =
    useGetPatientContactsByInquiryIdQuery(inquiryExternalId, {
      skip: !isOpen || !inquiryExternalId,
    });

  const patientTimeZone = toValidTimeZone(
    patient?.timezoneValue || patient?.timezone,
  );

  const lovedOne = inquiryContactsResponse?.data?.find(
    (contact) => contact.isPrimaryContact && !contact.isReferringProvider,
  );
  const lovedOneTimeZone = toValidTimeZone(lovedOne?.timezoneValue);
  const referrerTimeZone = toValidTimeZone(
    inquiryContactsResponse?.data?.find(
      (contact) => contact.isReferringProvider,
    )?.timezoneValue,
  );

  const timeZoneOptions = useMemo(
    () =>
      buildTimeZoneOptions(
        [
          { name: 'Patient', timeZone: patientTimeZone },
          {
            name: lovedOne?.relationToPatient
              ? `Inquirer (${lovedOne.relationToPatient})`
              : 'Inquirer',
            timeZone: lovedOneTimeZone,
          },
          { name: 'Referrer', timeZone: referrerTimeZone },
        ],
        dateRange,
      ),
    [
      patientTimeZone,
      lovedOneTimeZone,
      lovedOne?.relationToPatient,
      referrerTimeZone,
      dateRange,
    ],
  );

  const [selectedTimeZone, setSelectedTimeZone] = useState<string>();
  const timeZone =
    selectedTimeZone ??
    patientTimeZone ??
    lovedOneTimeZone ??
    referrerTimeZone ??
    SYSTEM_TIME_ZONE;

  useEffect(() => {
    if (!isOpen) {
      setDateRange(undefined);
      setSelectedTimeZone(undefined);
      return;
    }

    const now = DateTime.now();
    setDateRange({
      start: now.plus({ hours: 1 }).toJSDate(),
      end: now.plus({ days: 7 }).endOf('day').toJSDate(),
    });
  }, [isOpen]);

  const {
    data: providerAvailabilityData,
    isFetching: isProviderAvailabilityLoading,
  } = useGetProviderAvailabilityV2Query(
    {
      appointmentType: AppointmentTypeName.ADMISSION_CONSULTATION,
      start: dateRange?.start.toISOString(),
      end: dateRange?.end.toISOString(),
      limit: 50,
      patientId: patient.userId,
      inquiryId: inquiryExternalId,
    },
    {
      skip: !dateRange || !isOpen,
    },
  );

  const formattedSchedule = useMemo(() => {
    if (!providerAvailabilityData?.availableSlots) {
      return { slots: [], user_ids: [] };
    }
    return getGroupedTimeSlots(
      providerAvailabilityData.availableSlots,
      timeZone,
    );
  }, [providerAvailabilityData, timeZone]);

  useEffect(() => {
    const fetchProviders = async () => {
      const userIds = formattedSchedule.user_ids;
      if (userIds.length === 0) return;

      try {
        const providerDetails = await getAllProviderDetails({
          id: userIds,
          'include-avatar': true,
          'page-number': 1,
          'page-size': userIds.length,
          'include-provider-history': true,
        }).unwrap();

        setProviderInfo(providerDetails.data || []);
      } catch (error) {
        console.error('Failed to fetch provider details:', error);
      }
    };

    fetchProviders();
  }, [formattedSchedule.user_ids.join(','), getAllProviderDetails]);

  const selectedSlotDetails = useMemo(() => {
    if (!selectedSlot) return null;

    const provider = providerInfo.find(
      (p) => p.userId === selectedSlot.providerId,
    );

    if (!provider) return null;

    return {
      patient,
      provider,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
    };
  }, [selectedSlot, providerInfo, patient]);

  const handleClose = () => {
    setCurrentStep('slotSelection');
    setSelectedSlot(undefined);
    onClose();
  };

  const handleSlotSelect = (
    slotPayload: {
      providerId: string;
      startTime: string;
      endTime: string;
      slotId: string;
    },
    provider: GetProviderByIdResponse,
    slot: { originalStart: string; originalEnd: string },
  ) => {
    trackEvent(InquiryEvents.admissionConsultationSlotSelected, {
      inquiryId: inquiryExternalId,
      source: AnalyticsSourceValues.INQUIRY_ADMISSIONS_CONSULTATION,
      providerId: provider.userId,
      startTime: slot.originalStart,
      endTime: slot.originalEnd,
    });
    setSelectedSlot(slotPayload);
  };

  const handleContinueButtonClick = () => {
    trackEvent(InquiryEvents.admissionConsultationContinueClicked, {
      inquiryId: inquiryExternalId,
      source: AnalyticsSourceValues.INQUIRY_ADMISSIONS_CONSULTATION,
    });
    setCurrentStep('confirmation');
  };

  const handleBackButtonClick = () => {
    trackEvent(InquiryEvents.admissionConsultationBackClicked, {
      inquiryId: inquiryExternalId,
      source: AnalyticsSourceValues.INQUIRY_ADMISSIONS_CONSULTATION,
    });
    setCurrentStep('slotSelection');
  };

  const onConfirmButtonClick = async () => {
    if (!selectedSlotDetails) return;

    const { patient, provider, startTime, endTime } = selectedSlotDetails;

    if (Object.values(selectedSlotDetails).some((value) => !value)) {
      return;
    }

    const errorToast = () => {
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: 'admission-consultation-error_toast',
        text: 'Failed to add admissions consultation.',
        variant: 'critical',
      });
    };

    const appointmentStartDateTime = toISO(startTime);
    const appointmentEndDateTime = toISO(endTime);

    if (!appointmentStartDateTime || !appointmentEndDateTime) {
      errorToast();
      return;
    }

    const payload = {
      appointmentEndDateTime,
      appointmentStartDateTime,
      appointmentType: AppointmentTypeName.ADMISSION_CONSULTATION,
      hostExternalId: provider?.userId,
      ignoreAvailabilitySettings: false,
      isPatientAttending: true,
      patientExternalId: patient.userId,
      providerAttendeeExternalIds: [],
      recurrenceFrequency: Recurrence.DOES_NOT_REPEAT,
      status: AppointmentStatus.SCHEDULED,
      supportAttendeeExternalIds: [],
      timeZoneId: timeZone,
      inquiryExternalId,
    };

    try {
      const saveResult = await saveAppointment(payload);

      if ('error' in saveResult) {
        errorToast();
        return;
      }

      trackEvent(InquiryEvents.admissionConsultationConfirmed, {
        inquiryId: inquiryExternalId,
        source: AnalyticsSourceValues.INQUIRY_ADMISSIONS_CONSULTATION,
        providerId: provider.userId,
        startTime,
        endTime,
      });

      if (editingAppointmentID) {
        const cancelResult = await deleteAppointment({
          cancelType: RecurringAppointmentUpdateOption.ONLY,
          appointmentExternalId: editingAppointmentID,
          cancellationReason: 'PROVIDER_EDIT',
          inquiryExternalId,
        });

        if ('error' in cancelResult) {
          createToast({
            icon: FAIconName.exclamationTriangleRegular,
            id: 'admission-consultation-reschedule-error_toast',
            text: 'Added new admissions consultation, but failed to cancel the original appointment.',
            variant: 'critical',
          });
          handleClose();
          return;
        }
      }

      createToast({
        icon: FAIconName.checkRegular,
        id: 'admission-consultation-success_toast',
        text: `${editingAppointmentID ? 'Rescheduled' : 'Added'} admissions consultation successfully.`,
        variant: 'success',
      });
      handleClose();
    } catch {
      errorToast();
    }
  };

  const modalFooter = useMemo(() => {
    if (currentStep === 'confirmation') {
      return (
        <ChakraBox display="flex" justifyContent="space-between" width="100%">
          <FireflyButton
            id="admission-consultation-back-button"
            variant="secondary"
            leftIcon={FAIconName.arrowLeftRegular}
            isDisabled={isSubmitting}
            onClick={handleBackButtonClick}
          >
            Back
          </FireflyButton>
          <FireflyButton
            id="admission-consultation-save-button"
            variant="primary"
            rightIcon={FAIconName.checkRegular}
            isLoading={isSubmitting}
            onClick={onConfirmButtonClick}
          >
            Confirm
          </FireflyButton>
        </ChakraBox>
      );
    }

    return (
      <ChakraBox display="flex" justifyContent="flex-end" padding="16px">
        <FireflyButton
          id="admission-consultation-save-button"
          variant="primary"
          rightIcon={FAIconName.chevronRightRegular}
          isDisabled={!selectedSlot}
          onClick={handleContinueButtonClick}
        >
          Continue
        </FireflyButton>
      </ChakraBox>
    );
  }, [currentStep, selectedSlot, isSubmitting, editingAppointmentID]);

  const isReady =
    !isProviderAvailabilityLoading &&
    !isProviderDetailsLoading &&
    (formattedSchedule.slots.length === 0 || providerInfo.length > 0);

  const slotSelectionStep = () => (
    <SkeletonLoader id="admission-consultation-skeleton" isLoaded={isReady}>
      {isReady ? (
        <ChakraBox display="flex" flexDirection="column" height="55vh">
          {timeZoneOptions.length > 0 && (
            <ChakraHStack spacing="8px" mb="24px" flexWrap="wrap">
              <Text variant="body" color="neutral.secondary">
                Times shown in
              </Text>
              {timeZoneOptions.length === 1 ? (
                <Text id="admission-consultation-timezone" variant="bodyMedium">
                  {timeZoneOptions[0].title}
                </Text>
              ) : (
                timeZoneOptions.map((option) => (
                  <Tab
                    id={`admission-consultation-timezone-${option.zone}`}
                    isActive={option.zone === timeZone}
                    key={option.zone}
                    onClick={() => {
                      setSelectedTimeZone(option.zone);
                      setSelectedSlot(undefined);
                    }}
                    size="small"
                    title={option.title}
                    variant="bubble"
                  />
                ))
              )}
            </ChakraHStack>
          )}
          <ChakraBox flex="1" overflowY="auto">
            {formattedSchedule.slots.map((group) => (
              <ChakraVStack key={group.dateKey} spacing="12px" mb="24px">
                <ChakraHStack
                  justifyContent="start"
                  spacing="12px"
                  alignItems="start"
                  width="100%"
                >
                  <Text variant="bodyMedium">{group.dateLabel}</Text>
                  <Text variant="body" color="neutral.secondary">
                    {group.relativeLabel}
                  </Text>
                </ChakraHStack>
                <ChakraHStack
                  spacing="12px"
                  flexWrap="wrap"
                  justifyContent="start"
                  width="100%"
                >
                  {group.slots.map((slot) => {
                    const provider = providerInfo.find(
                      (p) => p.userId === slot.user_id,
                    );

                    const payload = {
                      providerId: provider.userId,
                      startTime: slot.originalStart,
                      endTime: slot.originalEnd,
                      slotId: slot.slotId,
                    };

                    return (
                      <TimeSlot
                        id={`time-slot-${slot.originalStart}-${slot.user_id}`}
                        key={`time-slot-${slot.originalStart}-${slot.user_id}`}
                        width="192px"
                        slot={{
                          ...payload,
                          isSelected: selectedSlot?.slotId === slot.slotId,
                          formattedStartEndTime: slot.timeLabel,
                          isPatientAvailabilityPreferenceMatch: false,
                        }}
                        provider={provider}
                        onSelect={() =>
                          handleSlotSelect(payload, provider, slot)
                        }
                        patientAvailabilityPreferences={[]}
                      />
                    );
                  })}
                </ChakraHStack>
              </ChakraVStack>
            ))}
          </ChakraBox>
        </ChakraBox>
      ) : (
        <ChakraBox height="55vh" />
      )}
    </SkeletonLoader>
  );

  const modalTitle = useMemo(
    () => renderModalTitle({ currentStep, isEditing: !!editingAppointmentID }),
    [currentStep, editingAppointmentID],
  );

  return (
    <ModalV2
      onClose={handleClose}
      isOpen={isOpen}
      size="5xl"
      title={modalTitle}
      footerContent={modalFooter}
    >
      {currentStep === 'confirmation'
        ? selectedSlotDetails && (
            <AppointmentSummary {...selectedSlotDetails} timeZone={timeZone} />
          )
        : slotSelectionStep()}
    </ModalV2>
  );
};

export default AdmissionConsultationModal;
